import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { ActivityScoreItem } from '../../types/Admin.api.type';
import { AccessDots } from './AccessDots';

interface ActivityScoreManagementProps {
  onSelectUser: (userId: number) => void;
}

const WEIGHT = {
  attendance: 1,
  vote: 2,
  adWatch: 2,
  question: 5,
  comment: 1,
  bookmark: 1,
  analysis: 1,
  exchange: 2,
  streak: 3,
} as const;

export const ActivityScoreManagement: React.FC<ActivityScoreManagementProps> = ({ onSelectUser }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [mobileVisiblePages, setMobileVisiblePages] = useState(1);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['adminActivityScores', currentPage],
    queryFn: () => adminApi.getActivityScores(currentPage, pageSize).then((res) => res.data),
  });

  const items: ActivityScoreItem[] = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  // 모바일: 여러 페이지 데이터 누적 조회
  const { data: mobileData } = useQuery({
    queryKey: ['adminActivityScoresMobile', mobileVisiblePages],
    queryFn: async () => {
      const promises = Array.from({ length: mobileVisiblePages }, (_, i) =>
        adminApi.getActivityScores(i, pageSize).then((res) => res.data),
      );
      const results = await Promise.all(promises);
      return {
        content: results.flatMap((r) => r.content),
        totalElements: results[0]?.totalElements ?? 0,
      };
    },
  });

  const mobileItems = mobileData?.content ?? [];
  const mobileTotalElements = mobileData?.totalElements ?? 0;

  if (isLoading) {
    return <div className="p-6 text-center">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <h2 className="text-lg lg:text-xl font-bold text-[#030213] mb-2">활동점수</h2>
        <p className="text-sm text-[#6B7280]">총 {totalElements}명</p>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#f3f3f5]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  사용자
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  출석
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.attendance}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  투표
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.vote}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  광고
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.adWatch}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  글작성
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.question}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  댓글
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.comment}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  즐겨찾기
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.bookmark}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  분석
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.analysis}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  꺼내기
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.exchange}</span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] tracking-wider">
                  연속출석
                  <br />
                  <span className="text-[10px] font-normal">x{WEIGHT.streak}</span>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  총점
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E5E7EB]">
              {items.map((item, idx) => (
                <tr key={item.userId} className="hover:bg-[#f3f3f5] transition-colors">
                  <td className="px-4 py-4 text-sm font-medium text-[#6B7280]">{currentPage * pageSize + idx + 1}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#f3f3f5] flex items-center justify-center overflow-hidden">
                        {item.profileImageUrl ? (
                          <img
                            src={item.profileImageUrl}
                            alt={item.userName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-[#6B7280] font-medium">
                            {item.userName?.charAt(0)?.toUpperCase() ?? '?'}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => onSelectUser(item.userId)}
                          className="text-sm font-medium text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0 text-left"
                        >
                          {item.userName}
                        </button>
                        <AccessDots access={item.recentAccess} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.attendanceCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.voteCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.adWatchCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.questionCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.commentCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.bookmarkCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.analysisCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.exchangeCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.streakCount}</td>
                  <td className="px-4 py-4 text-right font-mono text-sm font-bold text-blue-600">{item.totalScore}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-sm text-[#6B7280]">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {mobileItems.map((item, idx) => (
          <div key={item.userId} className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#f3f3f5] flex items-center justify-center text-xs font-bold text-[#6B7280]">
                {idx + 1}
              </div>
              <div className="w-10 h-10 rounded-full bg-[#f3f3f5] flex items-center justify-center overflow-hidden">
                {item.profileImageUrl ? (
                  <img src={item.profileImageUrl} alt={item.userName} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <span className="text-[#6B7280] font-medium">{item.userName?.charAt(0)?.toUpperCase() ?? '?'}</span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => onSelectUser(item.userId)}
                    className="text-sm font-medium text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
                  >
                    {item.userName}
                  </button>
                  <span className="font-mono text-sm font-bold text-blue-600">{item.totalScore}점</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-[#6B7280]">
                  <span>출석 {item.attendanceCount}</span>
                  <span>투표 {item.voteCount}</span>
                  <span>글 {item.questionCount}</span>
                  <span>연속 {item.streakCount}</span>
                </div>
                <div className="mt-1">
                  <AccessDots access={item.recentAccess} />
                </div>
              </div>
            </div>
          </div>
        ))}
        {mobileVisiblePages * pageSize < mobileTotalElements && (
          <button
            onClick={() => setMobileVisiblePages((prev) => prev + 1)}
            className="w-full py-3 text-sm font-medium text-[#030213] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] transition-colors"
          >
            더 보기
          </button>
        )}
      </div>
    </div>
  );
};
